"use client";
import React, { useState } from 'react';
import { toast } from "react-toastify";
import { apiSubscribeNewsletter } from '@/apis/page_optimization';

interface Props {
  companyId: string;
}

const Newsletter = ({ companyId }: Props) => {
  const [email, setEmail] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address.");
      return;
    }
    if (!validateEmail(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      const response = await apiSubscribeNewsletter(companyId, email);
      if (response.message === "Subscribed successfully") {
        toast.success("Thank you for subscribing!", { autoClose: 2000 });
        setEmail("");
      } else {
        toast.error("You have already subscribed to our newsletter.");
      }
    } catch (error) {
      console.error("Error subscribing:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative w-full py-12 md:py-16 bg-[#F8FAFC] overflow-hidden">
      <div className="container mx-auto px-6 sm:px-8 lg:px-12">
        {/* Main Newsletter Card */}
        <div className="relative rounded-2xl overflow-hidden">
          {/* Background Gradient */}
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(135deg, #1E3A5F 0%, #142A45 50%, #F97316 100%)'
            }}
          />

          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#F97316] opacity-10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#F97316] opacity-10 rounded-full blur-3xl" />

          {/* Content Grid */}
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center p-8 md:p-12 lg:p-16">

            {/* Left: Content */}
            <div className="text-center lg:text-left space-y-4">
              {/* Eyebrow */}
              <div className="flex items-center gap-2 justify-center lg:justify-start mb-2">
                <span className="w-1.5 h-6 rounded-full inline-block bg-[#F97316]" />
                <span className="text-[11px] font-black uppercase tracking-[0.25em] text-[#F97316]">
                  Exclusive Savings
                </span>
              </div>

              {/* Heading */}
              <h2 className="text-3xl md:text-4xl font-extrabold text-white leading-tight tracking-tight">
                Smarter <span className="text-[#F97316]">Shopping,</span> Delivered
              </h2>

              {/* Description */}
              <p className="text-[15px] md:text-base text-gray-300 leading-relaxed max-w-md mx-auto lg:mx-0">
                Get useful buying guides, product discoveries, comparisons and selected offers delivered to your inbox.
              </p>

              {/* Features List */}
              <div className="flex flex-col gap-3 pt-4">
                <div className="flex items-center gap-3 justify-center lg:justify-start">
                  <div className="w-6 h-6 rounded-full bg-[#15803D] flex items-center justify-center shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-white">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-sm text-white font-medium">Daily exclusive coupons & deals</span>
                </div>
                <div className="flex items-center gap-3 justify-center lg:justify-start">
                  <div className="w-6 h-6 rounded-full bg-[#15803D] flex items-center justify-center shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-white">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-sm text-white font-medium">Early access to flash sales</span>
                </div>
                <div className="flex items-center gap-3 justify-center lg:justify-start">
                  <div className="w-6 h-6 rounded-full bg-[#15803D] flex items-center justify-center shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-white">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-sm text-white font-medium">Unsubscribe anytime, no spam</span>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="w-full">
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-2xl border border-[#E5E7EB]">
                <form onSubmit={handleSubscribe} className="space-y-4">
                  {/* Email Input */}
                  <div>
                    <label htmlFor="newsletter-email" className="block text-sm font-semibold text-[#111827] mb-2">
                      Email Address
                    </label>
                    <input
                      id="newsletter-email"
                      type="email"
                      placeholder="Enter your email"
                      className="w-full px-4 py-3.5 rounded-xl border border-[#E5E7EB] text-[#111827] text-[15px] outline-none focus:border-[#F97316] focus:ring-2 focus:ring-[#F97316]/20 transition-all"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                    />
                  </div>

                  {/* Subscribe Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#F97316] text-white px-6 py-4 rounded-xl text-[15px] font-bold hover:bg-[#EA580C] transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-[#F97316]/20"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Subscribing...</span>
                      </>
                    ) : (
                      <>
                        <span>Subscribe Now</span>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                          <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                        </svg>
                      </>
                    )}
                  </button>

                  {/* Privacy Notice */}
                  <p className="text-xs text-[#4B5563] text-center leading-relaxed">
                    We respect your privacy. No spam, unsubscribe anytime.
                  </p>
                </form>

                {/* Trust Badge */}
                <div className="mt-6 pt-6 border-t border-[#E5E7EB] flex items-center justify-center gap-2 text-sm text-[#4B5563]">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-[#15803D]">
                    <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">100% secure and private</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;